import { useCallback, useEffect, useRef } from 'react';
import { useScopeCache } from './useScopeCache';
import { isEmptyScope } from '../utils/scopeSelection';

/*
 * ТЗ №05 / FIX-05: экран возвращается к последнему выбранному scope, если его
 * открыли без scope-параметров в URL (пункт меню, смена вкладки).
 *
 * Правила:
 *  - scope в URL всегда главнее кэша; при каждом появлении scope в URL он запоминается;
 *  - кэш обновляется только явным remember() (применение или сброс фильтра),
 *    поэтому пустой URL после «Сбросить» не восстанавливает старый выбор;
 *  - сам URL правит страница: restorableScope — это то, что нужно в него вернуть.
 */
export const useScopeRestore = (screenKey, urlScope, { enabled = true } = {}) => {
  const { scope: cachedScope, setScope } = useScopeCache(screenKey);
  // Запоминаем scope из URL каждый раз, когда он меняется на непустой (вход по
  // ссылке, Back/Forward). Эффект завязан на содержимое URL, а не на кэш: иначе
  // сразу после «Сбросить» (кэш уже пуст, URL ещё старый) он вернул бы старый выбор.
  const urlKey = JSON.stringify(urlScope);
  const urlScopeRef = useRef(urlScope);
  urlScopeRef.current = urlScope;
  useEffect(() => {
    if (!enabled) return;
    if (!isEmptyScope(urlScopeRef.current)) setScope(urlScopeRef.current);
  }, [enabled, urlKey, setScope]);

  const remember = useCallback((scope) => setScope(scope), [setScope]);

  const restorable = enabled && isEmptyScope(urlScope) && !isEmptyScope(cachedScope);
  return { restorableScope: restorable ? cachedScope : null, remember };
};

export default useScopeRestore;
